import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { useState } from 'react';

import { HubCard } from '@/Pages/HubRepo/HubCard';
import { Code } from '@/components/Code/Code';
import { Text } from '@/design-system/components/Text';
import CodeIcon from '@/icons/CodeIcon.svg?react';
import { generatePullCode } from '@/utils/hub';

import { CopyIconButton } from '../CopyButton/CopyButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';

const LANGUAGE_OPTIONS: {
  name: string;
  language: 'py' | 'js';
}[] = [
  { name: 'Python SDK', language: 'py' },
  { name: 'JavaScript SDK', language: 'js' },
];

function LanguageDropdown({
  language,
  setLanguage,
}: {
  language: 'py' | 'js';
  setLanguage: (language: 'py' | 'js') => void;
}) {
  const selectedName = LANGUAGE_OPTIONS.find((mode) => {
    return mode.language === language;
  })?.name;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="relative inline-flex h-7 cursor-pointer items-center justify-start whitespace-nowrap rounded-md border border-secondary px-2 hover:bg-secondary-hover hover:bg-slate-100 active:bg-secondary hover:dark:bg-slate-800">
        <div className="inline-flex select-none gap-1 text-center text-sm font-medium leading-tight text-slate-900 dark:text-white">
          {selectedName} <ChevronDownIcon className="my-auto h-4 w-4" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {LANGUAGE_OPTIONS.map((mode, i) => (
          <DropdownMenuItem key={i} onClick={() => setLanguage(mode.language)}>
            {mode.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const HubCode = ({
  repo,
  owner,
  commit,
  isPublic,
}: {
  repo: string;
  owner: string;
  commit?: string;
  isPublic?: boolean;
}) => {
  const [language, setLanguage] = useState<'py' | 'js'>('py');
  const code = generatePullCode(owner, repo, commit, isPublic, language);
  return (
    <div className="flex flex-col gap-3">
      <HubCard
        title="Use object in LangChain"
        actions={
          <>
            <div className="flex gap-2 transition-all">
              <CopyIconButton copy={code ?? ''} className={'p-1.5'} />
            </div>
            <LanguageDropdown language={language} setLanguage={setLanguage} />
          </>
        }
        icon={<CodeIcon />}
      >
        <div className="relative -m-4">
          <Code
            readOnly
            language={language === 'py' ? python() : javascript()}
            value={code}
          />
        </div>
      </HubCard>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <Text variant="md" as="span">
          For more examples on using prompts in code, see{' '}
          <a
            href="https://docs.langchain.com/langsmith/manage-prompts-programmatically"
            target="_blank"
            className="text-secondary underline"
            rel="noopener noreferrer"
          >
            Managing prompts programatically
          </a>
          .
          <br />
          <br />
        </Text>
        {isPublic && (
          <Text variant="sm" as="span">
            Disclaimer: Prompts are user-generated and unverified. LangChain
            does not review or endorse public prompts, use these at your own
            risk. Use of Prompt Hub is subject to our{' '}
            <a
              href="https://www.langchain.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Terms of Service
            </a>
            .
          </Text>
        )}
      </div>
    </div>
  );
};
